/**
 * ecosystemAdapter — pure adapter functions that map the live
 * `/api/integrations/ecosystem/*` payloads into the data shape the
 * JTR roadmap/changelog UI kit expects.
 *
 * Adapter rules (from owner directive 2026-02-02):
 *   1. Live API is the source of truth.
 *   2. Only publicDisplayApproved=true items are exposed publicly.
 *   3. Never expose raw upstream payloads — pick only the fields the
 *      kit needs.
 *   4. Quarters are synthesized from `sortDate` when the API returns
 *      `quarter: null`.
 *   5. Bilingual fields {en, ja} get the API's language-tagged value
 *      in the requested lang, and the SAME string is used for the
 *      other lang slot when only one is available (the kit always
 *      reads `pickLang(field, lang)`).
 *   6. NoJo brand-colors are kept stable on a serviceId basis so the
 *      timeline strip stays visually consistent regardless of the
 *      upstream item's `serviceColor` string.
 */

// Stable brand-color anchors used for category badges. Keeping these
// matched to /app/frontend/src/data/navigation.js so the timeline
// echoes the rest of the corporate site.
export const SERVICE_CATEGORY_META = {
  japanetccard: {
    en: "JapanETCcard.com",
    ja: "JapanETCcard.com",
    color: "violet",
  },
  japantollreceipts: {
    en: "JapanTollReceipts.jp",
    ja: "JapanTollReceipts.jp",
    color: "emerald",
  },
  japanchangemoney: {
    en: "JapanChangeMoney.com",
    ja: "JapanChangeMoney.com",
    color: "sky",
  },
  "nojo-enterprise": {
    en: "NoJo Enterprise",
    ja: "NoJo Enterprise",
    color: "indigo",
  },
};

// Canonical status display config — matches the kit's STATUS_META keys
// 1-for-1 so the kit's SVG drawing code, mobile layout, and pill colors
// all "just work". `under_review` / `deferred` / `released` are coerced
// upstream by `mapStatus()` below so the kit only ever sees one of the
// five keys it understands.
export const STATUS_META = {
  completed: { en: "Completed", ja: "完了", color: "emerald", dot: "#10b981" },
  in_testing: { en: "In Testing", ja: "検証中", color: "sky", dot: "#0ea5e9" },
  in_progress: { en: "In Progress", ja: "実装中", color: "fuchsia", dot: "#d946ef" },
  planned: { en: "Planned", ja: "予定", color: "amber", dot: "#f59e0b" },
  launch: { en: "Launch", ja: "ローンチ", color: "rose", dot: "#e11d48" },
};

function mapStatus(raw) {
  switch (raw) {
    case "completed":
    case "in_testing":
    case "in_progress":
    case "planned":
    case "launch":
      return raw;
    case "released":
      return "completed";
    case "under_review":
      return "in_progress";
    case "deferred":
      return "planned";
    default:
      return "planned";
  }
}

function quarterFromIsoOrLabel(value) {
  if (!value) return null;
  // Already a "qN_YYYY" / "QN YYYY" / "YYYY-QN" / similar string?
  const direct = String(value).match(/q?(\d)[\s_-]*(\d{4})/i);
  if (direct) return `q${direct[1]}_${direct[2]}`;
  // Try ISO date.
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const q = Math.floor(d.getMonth() / 3) + 1;
  return `q${q}_${d.getFullYear()}`;
}

function quarterLabel(key) {
  // Convert "q1_2026" → { en: "Q1 2026", ja: "2026年第1四半期" }
  const m = key.match(/q(\d)_(\d{4})/);
  if (!m) {
    return { en: key, ja: key };
  }
  const [, q, year] = m;
  return {
    en: `Q${q} ${year}`,
    ja: `${year}年第${q}四半期`,
  };
}

function safeBilingual(value, lang) {
  // The upstream returns a single-language string already in the
  // requested lang. To satisfy the kit's `pickLang(field, lang)` we
  // wrap it as { en, ja } where both slots carry the same value.
  // Future: have the upstream return `{ en, ja }` directly and we'll
  // pass it through unchanged.
  const v = value || "";
  if (lang === "ja") return { en: v, ja: v };
  return { en: v, ja: v };
}

/**
 * Map roadmap API items → kit MILESTONES[] shape.
 *
 * Returns:
 *   {
 *     milestones: [...kit-shape items],
 *     quarters:  [...{ key, label: { en, ja } }],
 *     categoryMeta: { [serviceId]: { en, ja, color } },
 *   }
 */
export function mapEcosystemRoadmap(apiPayload, lang) {
  if (!apiPayload || !Array.isArray(apiPayload.items)) {
    return { milestones: [], quarters: [], categoryMeta: { ...SERVICE_CATEGORY_META } };
  }

  // Only public-approved items are exposed in the public UI.
  const items = apiPayload.items.filter((it) => it.publicDisplayApproved !== false);

  // Build quarter index — synthesize when API quarter is null.
  const quarterKeys = new Set();
  const milestones = items.map((it, idx) => {
    const qKey =
      quarterFromIsoOrLabel(it.quarter) ||
      quarterFromIsoOrLabel(it.sortDate) ||
      quarterFromIsoOrLabel(it.date) ||
      "unscheduled";
    quarterKeys.add(qKey);
    return {
      id: `${it.serviceId || "unknown"}-${it.id || idx}`,
      reviewed: null,
      quarter: qKey,
      status: mapStatus(it.status),
      category: it.serviceId || "nojo-enterprise",
      title: safeBilingual(it.title, lang),
      desc: safeBilingual(it.publicSummary || it.summary || "", lang),
      impact: null,
      eta: null,
      progress: null,
      // Extra side-channel for adapter-driven badges (kit ignores these)
      _serviceId: it.serviceId,
      _serviceColorHex: it.serviceColorHex,
      _sortDate: it.sortDate || it.date || null,
    };
  });

  // Order quarters chronologically (q1_2026 < q2_2026 < … < unscheduled).
  const sortedKeys = Array.from(quarterKeys).sort((a, b) => {
    if (a === "unscheduled") return 1;
    if (b === "unscheduled") return -1;
    const ma = a.match(/q(\d)_(\d{4})/);
    const mb = b.match(/q(\d)_(\d{4})/);
    if (!ma || !mb) return a.localeCompare(b);
    const av = parseInt(mb[2], 10) * 10 + parseInt(mb[1], 10);
    const bv = parseInt(ma[2], 10) * 10 + parseInt(ma[1], 10);
    return bv - av; // chronological ascending
  });

  const quarters = sortedKeys.map((key) => ({
    key,
    label:
      key === "unscheduled"
        ? { en: "Unscheduled", ja: "未定" }
        : quarterLabel(key),
  }));

  // Build category meta — start with the canonical NoJo brand map, and
  // only retain serviceIds actually present in the live data so the
  // category-filter chips don't show empty options.
  const presentServiceIds = new Set(milestones.map((m) => m.category));
  const categoryMeta = {};
  Object.entries(SERVICE_CATEGORY_META).forEach(([sid, meta]) => {
    if (presentServiceIds.has(sid)) categoryMeta[sid] = meta;
  });
  // Always keep nojo-enterprise as a fallback bucket
  if (!categoryMeta["nojo-enterprise"]) {
    categoryMeta["nojo-enterprise"] = SERVICE_CATEGORY_META["nojo-enterprise"];
  }

  return { milestones, quarters, categoryMeta };
}

/**
 * Map changelog API items → kit RELEASES[] shape.
 *
 * Sorted newest-first by `sortDate`. Adapter passes through structured
 * `changes` / `features` / `fixes` arrays when the upstream provides
 * them; per owner directive, we never invent these — empty arrays are
 * intentionally preserved as empty so the UI can hide the section.
 */
export function mapEcosystemChangelog(apiPayload, lang) {
  if (!apiPayload || !Array.isArray(apiPayload.items)) return [];
  const items = apiPayload.items.filter((it) => it.publicDisplayApproved !== false);
  // Newest first.
  const sorted = [...items].sort((a, b) => {
    const ad = new Date(a.sortDate || a.date || 0).getTime();
    const bd = new Date(b.sortDate || b.date || 0).getTime();
    return bd - ad;
  });
  return sorted.map((it, idx) => ({
    version: it.version || `r${idx + 1}`,
    type: idx === 0 ? "Feature Release" : "Patch",
    date: it.date || it.sortDate || "",
    sortDate: it.sortDate || it.date || "",
    latest: idx === 0,
    title: safeBilingual(it.title, lang),
    desc: safeBilingual(it.publicSummary || it.summary || "", lang),
    // Pass through structured detail arrays if upstream provides them.
    // These are HIDDEN when empty (no fabrication per owner directive).
    changes: Array.isArray(it.changes) ? it.changes : [],
    features: Array.isArray(it.features) ? it.features : [],
    fixes: Array.isArray(it.fixes) ? it.fixes : [],
    improvements: Array.isArray(it.improvements) ? it.improvements : [],
    security: Array.isArray(it.security) ? it.security : [],
    notes: Array.isArray(it.notes) ? it.notes : [],
    tags: Array.isArray(it.tags) ? it.tags : [],
    // adapter side-channel
    _serviceId: it.serviceId,
    _serviceColorHex: it.serviceColorHex,
  }));
}
